/**
 * Admin pharmacy medication queue (M12-T06a, ADR-0028). hospital_admin only.
 * Every active request is listed (fan-out); the pharmacy answers to AMTC (not
 * the patient). AMTC curation is T06b.
 */
import { apiFetch } from './client'

export type AdminMedicationRequest = {
  reference: string
  quantity: string
  area: string | null
  urgency: string
  status: string
  patient_name: string | null
  patient_phone: string | null
  drug_generic_name: string
  drug_brand_name: string | null
  drug_strength: string
  drug_form: string
  prescription_only: boolean
  created_at: string
  expires_at: string
  my_available: boolean | null
  my_price_pesewas: number | null
}

export const MEDICATION_REQUESTS_KEY = ['admin', 'medication-requests'] as const

/** GET /api/v1/admin/medication-requests — the pharmacy's queue (server-scoped). */
export async function fetchMedicationQueue(
  token: string | null,
): Promise<AdminMedicationRequest[]> {
  const body = await apiFetch<{ data: AdminMedicationRequest[] }>(
    '/api/v1/admin/medication-requests',
    { token },
  )
  return body.data
}

export type AnswerInput = { available: boolean; price_pesewas: number | null }

/** POST /api/v1/admin/medication-requests/{ref}/answer — Available (price) / Not. */
export async function answerMedicationRequest(
  token: string | null,
  reference: string,
  input: AnswerInput,
): Promise<void> {
  await apiFetch(`/api/v1/admin/medication-requests/${reference}/answer`, {
    method: 'POST',
    token,
    body: {
      available: input.available,
      ...(input.available && input.price_pesewas != null
        ? { price_pesewas: input.price_pesewas }
        : {}),
    },
  })
}

// ─── AMTC curation (T06b) — amtc_admin only ────────────────────────────────

export type CurationResponse = {
  hospital_slug: string
  hospital_name: string
  city: string
  available: boolean
  price_pesewas: number | null
  distance_km: number | null
  answered_at: string
  suggested: boolean
}

export type CurationRequest = {
  reference: string
  status: string
  quantity: string
  area: string | null
  urgency: string
  patient_name: string | null
  patient_phone: string | null
  drug_generic_name: string
  drug_brand_name: string | null
  drug_strength: string
  drug_form: string
  prescription_only: boolean
  created_at: string
  answered_count: number
  responses: CurationResponse[]
}

export const MEDICATION_CURATION_KEY = ['admin', 'medication-curation'] as const

/** GET /api/v1/admin/medication-curation — all active requests + ranked answers. */
export async function fetchMedicationCuration(
  token: string | null,
): Promise<CurationRequest[]> {
  const body = await apiFetch<{ data: CurationRequest[] }>(
    '/api/v1/admin/medication-curation',
    { token },
  )
  return body.data
}

/** POST /api/v1/admin/medication-curation/{ref}/send-offer — send one pharmacy's
 * answer to the patient as the curated offer. */
export async function sendOffer(
  token: string | null,
  reference: string,
  hospitalSlug: string,
): Promise<void> {
  await apiFetch(`/api/v1/admin/medication-curation/${reference}/send-offer`, {
    method: 'POST',
    token,
    body: { hospital_slug: hospitalSlug },
  })
}
